import { Component, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Subscription } from 'rxjs';
import { PageBreadcrumbComponent } from '../../shared/components/common/page-breadcrumb/page-breadcrumb.component';
import { BadgeComponent } from '../../shared/components/ui/badge/badge.component';
import { ButtonComponent } from '../../shared/components/ui/button/button.component';
import { ModalComponent } from '../../shared/components/ui/modal/modal.component';
import { LabelComponent } from '../../shared/components/form/label/label.component';
import { InputFieldComponent } from '../../shared/components/form/input/input-field.component';
import { UbigeoPickerComponent } from '../../shared/components/form/ubigeo-picker/ubigeo-picker.component';
import { NotificationService } from '../../shared/components/ui/notification/notification.service';
import { CompanyService } from '../../services/company.service';
import { UbigeoService } from '../../services/ubigeo.service';
import { CompanyResponse } from '../../dto/company.response';
import { CompanyRequest } from '../../dto/company.request';
import { UbigeoResponse } from '../../dto/ubigeo.response';
import { AuthService } from '../../services/auth.service';
import { SidebarRefreshService } from '../../services/sidebar-refresh.service';

type Tab = 'general' | 'sunat' | 'certificate';

@Component({
    selector: 'app-companies',
    standalone: true,
    imports: [
        CommonModule,
        FormsModule,
        PageBreadcrumbComponent,
        BadgeComponent,
        ButtonComponent,
        ModalComponent,
        LabelComponent,
        InputFieldComponent,
        UbigeoPickerComponent,
    ],
    templateUrl: './companies.component.html',
})
export class CompaniesComponent implements OnInit, OnDestroy {
    companies: CompanyResponse[] = [];
    loadingTable = false;
    private sub = new Subscription();

    // Pagination
    currentPage = 1;
    itemsPerPage = 10;
    readonly pageSizeOptions = [10, 20, 50];

    // Search
    searchTerm = '';

    // Form modal
    showForm = false;
    isEditing = false;
    submitting = false;
    formSubmitted = false;
    editingId: number | null = null;
    activeTab: Tab = 'general';

    // — General —
    ruc = '';
    businessName = '';
    tradeName = '';
    address = '';
    ubigeo = '';

    // — Contact —
    phone = '';
    email = '';
    website = '';
    logoUrl = '';

    // — SUNAT config —
    sunatEstablishmentCode = '';
    sunatAmazoniaLaw = false;
    sunatProduction = false;
    ubigDepartment = '';
    ubigProvince = '';
    ubigDistrict = '';
    sunatDetractionAccount = '';

    // — SUNAT credentials —
    sunatSecondaryUser = '';
    sunatSecondaryUserPassword = '';
    sunatGuideId = '';
    sunatGuidePassword = '';

    // — Logo —
    logoFile: File | null = null;

    // — Certificate —
    pfxFile: File | null = null;
    pfxPassword = '';
    sunatCertificateLoaded: boolean | null = null;

    togglingStatusId: number | null = null;

    // Ubigeos
    allUbigeos: UbigeoResponse[] = [];
    loadingUbigeos = false;

    get filteredCompanies(): CompanyResponse[] {
        const t = this.searchTerm.trim().toLowerCase();
        if (!t) return this.companies;
        return this.companies.filter(c =>
            c.ruc.toLowerCase().includes(t) ||
            c.businessName.toLowerCase().includes(t) ||
            (c.tradeName ?? '').toLowerCase().includes(t)
        );
    }

    get totalPages(): number {
        return Math.max(1, Math.ceil(this.filteredCompanies.length / this.itemsPerPage));
    }

    get currentItems(): CompanyResponse[] {
        const start = (this.currentPage - 1) * this.itemsPerPage;
        return this.filteredCompanies.slice(start, start + this.itemsPerPage);
    }

    get formError(): string {
        if (!this.ruc.trim()) return 'RUC requerido';
        if (!/^\d{11}$/.test(this.ruc.trim())) return 'RUC debe tener 11 dígitos';
        if (!this.businessName.trim()) return 'Razón social requerida';
        if (!this.address.trim()) return 'Dirección requerida';
        if (!this.ubigeo.trim()) return 'Ubigeo requerido';
        return '';
    }

    get sunatFormError(): string {
        if (!this.sunatEstablishmentCode.trim()) return 'Código de establecimiento requerido';
        if (this.sunatProduction && this.sunatSecondaryUser.toUpperCase().includes('MODDATOS'))
            return 'En producción el usuario SUNAT no puede contener "MODDATOS"';
        return '';
    }

    private removeAccents(str: string): string {
        return str.normalize('NFD').replace(/\p{Diacritic}/gu, '').toUpperCase();
    }

    onUbigeoChange(code: string): void {
        this.ubigeo = code;
        const u = this.allUbigeos.find(u => u.ubigeo === code);
        this.ubigDepartment = u ? this.removeAccents(u.department) : '';
        this.ubigProvince = u ? this.removeAccents(u.province) : '';
        this.ubigDistrict = u ? this.removeAccents(u.district) : '';
    }

    constructor(
        private companyService: CompanyService,
        private ubigeoService: UbigeoService,
        private notify: NotificationService,
        private authService: AuthService,
        private sidebarRefresh: SidebarRefreshService,
    ) {}

    ngOnInit(): void {
        this.loadCompanies();
        this.loadUbigeos();
    }

    private loadUbigeos(): void {
        this.loadingUbigeos = true;
        this.ubigeoService.listActive().subscribe({
            next: res => { this.allUbigeos = res.data ?? []; this.loadingUbigeos = false; },
            error: () => { this.loadingUbigeos = false; }
        });
    }

    ngOnDestroy(): void {
        this.sub.unsubscribe();
    }

    loadCompanies(): void {
        this.loadingTable = true;
        const s = this.companyService.getAll().subscribe({
            next: res => { this.companies = res.data ?? []; this.loadingTable = false; },
            error: () => { this.notify.error('Error al cargar empresas'); this.loadingTable = false; }
        });
        this.sub.add(s);
    }

    setSearchTerm(val: string): void {
        this.searchTerm = val;
        this.currentPage = 1;
    }

    goToPage(page: number): void {
        if (page < 1 || page > this.totalPages) return;
        this.currentPage = page;
    }

    onPageSizeChange(size: number): void {
        this.itemsPerPage = size;
        this.currentPage = 1;
    }

    openCreate(): void {
        this.isEditing = false;
        this.editingId = null;
        this.formSubmitted = false;
        this.activeTab = 'general';
        this.resetForm();
        this.showForm = true;
    }

    openEdit(company: CompanyResponse): void {
        this.isEditing = true;
        this.editingId = company.id;
        this.formSubmitted = false;
        this.activeTab = 'general';
        this.ruc = company.ruc;
        this.businessName = company.businessName;
        this.tradeName = company.tradeName ?? '';
        this.address = company.address ?? '';
        this.ubigeo = company.ubigeo ?? '';
        this.phone = company.phone ?? '';
        this.email = company.email ?? '';
        this.website = company.website ?? '';
        this.logoUrl = company.logoUrl ?? '';
        this.sunatEstablishmentCode = company.sunatEstablishmentCode ?? '';
        this.sunatAmazoniaLaw = company.sunatAmazoniaLaw ?? false;
        this.sunatProduction = company.sunatProduction ?? false;
        this.ubigDepartment = company.ubigDepartment ? this.removeAccents(company.ubigDepartment) : '';
        this.ubigProvince = company.ubigProvince ? this.removeAccents(company.ubigProvince) : '';
        this.ubigDistrict = company.ubigDistrict ? this.removeAccents(company.ubigDistrict) : '';
        this.sunatDetractionAccount = company.sunatDetractionAccount ?? '';
        this.sunatSecondaryUser = company.sunatSecondaryUser ?? '';
        this.sunatGuideId = company.sunatGuideId ?? '';
        this.sunatCertificateLoaded = company.sunatCertificateLoaded ?? null;
        this.sunatSecondaryUserPassword = company.sunatSecondaryUserPassword ?? '';
        this.sunatGuidePassword = company.sunatGuidePassword ?? '';
        this.pfxFile = null;
        this.pfxPassword = '';
        this.showForm = true;
    }

    closeForm(): void {
        this.showForm = false;
    }

    onLogoFileChange(event: Event): void {
        const input = event.target as HTMLInputElement;
        this.logoFile = input.files?.[0] ?? null;
    }

    onPfxFileChange(event: Event): void {
        const input = event.target as HTMLInputElement;
        this.pfxFile = input.files?.[0] ?? null;
    }

    save(): void {
        this.formSubmitted = true;
        if (this.formError) {
            this.activeTab = 'general';
            return;
        }
        if (this.sunatFormError) {
            this.activeTab = 'sunat';
            return;
        }
        this.submitting = true;

        const req: CompanyRequest = {
            ruc: this.ruc.trim(),
            businessName: this.businessName.trim(),
            tradeName: this.tradeName.trim() || undefined,
            address: this.address.trim(),
            ubigeo: this.ubigeo.trim(),
            phone: this.phone.trim() || undefined,
            email: this.email.trim() || undefined,
            website: this.website.trim() || undefined,
            logoUrl: this.logoUrl.trim() || undefined,
            sunatEstablishmentCode: this.sunatEstablishmentCode.trim(),
            sunatAmazoniaLaw: this.sunatAmazoniaLaw,
            sunatProduction: this.sunatProduction,
            sunatSecondaryUser: this.sunatSecondaryUser.trim() || undefined,
            sunatSecondaryUserPassword: this.sunatSecondaryUserPassword.trim() || undefined,
            sunatGuideId: this.sunatGuideId.trim() || undefined,
            sunatGuidePassword: this.sunatGuidePassword.trim() || undefined,
            ubigDepartment: this.ubigDepartment.trim() || undefined,
            ubigProvince: this.ubigProvince.trim() || undefined,
            ubigDistrict: this.ubigDistrict.trim() || undefined,
            sunatDetractionAccount: this.sunatDetractionAccount.trim() || undefined,
        };

        const op$ = this.isEditing
            ? this.companyService.update(this.editingId!, req, this.pfxFile, this.pfxPassword.trim() || null, this.logoFile)
            : this.companyService.create(req, this.pfxFile, this.pfxPassword.trim() || null, this.logoFile);

        const savedId = this.editingId;
        const s = op$.subscribe({
            next: res => {
                this.notify.success(res.message ?? 'Guardado correctamente');
                this.submitting = false;
                this.showForm = false;
                this.loadCompanies();
                if (savedId === this.authService.companyId) {
                    this.sidebarRefresh.refreshLogo(res.data?.logoUrl ?? '');
                }
            },
            error: err => {
                this.notify.error(err?.error?.message ?? 'Error al guardar empresa');
                this.submitting = false;
            }
        });
        this.sub.add(s);
    }

    toggleStatus(company: CompanyResponse): void {
        const newStatus = company.status === 1 ? 0 : 1;
        this.togglingStatusId = company.id;
        const s = this.companyService.updateStatus(company.id, newStatus).subscribe({
            next: () => {
                company.status = newStatus;
                this.notify.success(`Empresa ${newStatus === 1 ? 'activada' : 'inactivada'}`);
                this.togglingStatusId = null;
            },
            error: err => {
                this.notify.error(err?.error?.message ?? 'Error al cambiar estado');
                this.togglingStatusId = null;
            }
        });
        this.sub.add(s);
    }

    private resetForm(): void {
        this.ruc = ''; this.businessName = ''; this.tradeName = '';
        this.address = ''; this.ubigeo = '';
        this.phone = ''; this.email = ''; this.website = ''; this.logoUrl = '';
        this.sunatEstablishmentCode = ''; this.sunatAmazoniaLaw = false;
        this.sunatProduction = false; this.ubigDepartment = '';
        this.ubigProvince = ''; this.ubigDistrict = ''; this.sunatDetractionAccount = '';
        this.sunatSecondaryUser = ''; this.sunatSecondaryUserPassword = '';
        this.sunatGuideId = ''; this.sunatGuidePassword = '';
        this.logoFile = null; this.pfxFile = null; this.pfxPassword = ''; this.sunatCertificateLoaded = null;
    }
}
